import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Compass,
  Filter,
  Gauge,
  HeartPulse,
  History,
  ScanSearch,
  Users,
  Waves,
} from 'lucide-react';
import { useRef } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';

import { replayToday, shiftWeekday, useReplayDate } from '../lib/useReplayDate';
import { cn } from '../lib/utils';

const consoleTabs = [
  { to: 'now', label: 'Now', icon: Compass, replay: true },
  { to: 'debrief', label: 'Debrief', icon: ClipboardList, replay: true },
  { to: 'health', label: 'Health', icon: HeartPulse, replay: false },
  { to: 'pulse', label: 'Pulse', icon: Gauge, replay: true },
  { to: 'flow', label: 'Flow', icon: Waves, replay: true },
  { to: 'crowding', label: 'Crowding', icon: Users, replay: true },
  { to: 'stocks', label: 'Stocks', icon: ScanSearch, replay: true },
  { to: 'screener', label: 'Screener', icon: Filter, replay: true },
] as const;

const stepButton =
  'inline-flex h-9 w-9 items-center justify-center rounded border border-border bg-surface hover:bg-surface-raised disabled:cursor-not-allowed disabled:opacity-40';

function ReplayControls() {
  const { asOf, setAsOf } = useReplayDate();
  const inputRef = useRef<HTMLInputElement>(null);
  const today = replayToday();
  const previous = shiftWeekday(asOf ?? today, -1);
  const next = asOf ? shiftWeekday(asOf, 1) : null;
  const canGoForward = next !== null && next <= today;
  return (
    <div className="mt-3 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <label
          htmlFor="market-console-replay"
          className="inline-flex items-center gap-1 text-xs text-text-muted"
        >
          <History className="h-3.5 w-3.5" aria-hidden="true" />
          Replay date
        </label>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Previous session"
            title="Previous session"
            onClick={() => setAsOf(previous)}
            className={stepButton}
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          </button>
          <input
            ref={inputRef}
            id="market-console-replay"
            type="date"
            max={today}
            value={asOf ?? ''}
            onClick={(event) => event.currentTarget.showPicker()}
            onChange={(event) => setAsOf(event.target.value || null)}
            className="h-9 cursor-pointer rounded border border-border bg-bg px-2 text-sm text-text [&::-webkit-calendar-picker-indicator]:hidden"
          />
          <button
            type="button"
            aria-label="Open calendar"
            title="Open calendar"
            onClick={() => inputRef.current?.showPicker()}
            className={stepButton}
          >
            <CalendarDays className="h-4 w-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            aria-label="Next session"
            title="Next session"
            disabled={!canGoForward}
            onClick={() => {
              if (next) setAsOf(next);
            }}
            className={stepButton}
          >
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        {asOf ? (
          <button
            type="button"
            onClick={() => setAsOf(null)}
            className="h-9 rounded border border-border bg-surface px-3 text-sm hover:bg-surface-raised"
          >
            Back to live
          </button>
        ) : null}
      </div>
      {asOf ? (
        <p
          role="status"
          className="rounded border border-warn/30 bg-warn/10 px-3 py-2 text-xs text-warn"
        >
          Replaying {asOf}: each section shows what was stored on or before that date, so weekends
          and holidays show the prior session. Health and the Dashboard always show live data.
        </p>
      ) : null}
    </div>
  );
}

export function MarketConsole() {
  const { pathname, search } = useLocation();
  const activeTab = consoleTabs.find((tab) => pathname.endsWith(`/${tab.to}`));
  return (
    <div className="bg-bg text-text">
      <div className="mx-auto max-w-7xl px-4 pt-5 sm:px-6">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">
          Market Console
        </div>
        <nav
          className="mt-2 flex gap-1 overflow-x-auto overflow-y-hidden border-b border-border"
          aria-label="Market console sections"
        >
          {consoleTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <NavLink
                key={tab.to}
                to={{ pathname: tab.to, search }}
                className={({ isActive }) =>
                  cn(
                    'flex min-h-11 items-center gap-2 whitespace-nowrap border-b-2 px-3 text-sm transition',
                    isActive
                      ? 'border-text font-semibold text-text'
                      : 'border-transparent text-text-muted hover:text-text',
                  )
                }
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                {tab.label}
              </NavLink>
            );
          })}
        </nav>
        {activeTab?.replay ? <ReplayControls /> : null}
      </div>
      <Outlet />
    </div>
  );
}
